import * as R from 'remeda';
import * as z from 'zod';
import { DatasetAPISchema } from '@rossum/mdh-api-client';

const datasetMetadataSchema = z.object({
  name: z.string().catch(''),
  description: z.string().catch(''),
  manual: z.boolean().catch(false),
  columnNames: z.record(z.string()).catch({}),
});

export type ElisColumnType =
  | 'string'
  | 'number'
  | 'date'
  | 'object'
  | 'array'
  | 'bool'
  | 'unknown';

// The types a column can be declared with; inferred columns may also be
// objects, arrays or unknown.
export const declaredColumnTypes = [
  'string',
  'number',
  'date',
  'bool',
] as const satisfies readonly ElisColumnType[];
export type DeclaredColumnType = (typeof declaredColumnTypes)[number];

export const isDeclaredColumnType = (
  value: unknown
): value is DeclaredColumnType =>
  declaredColumnTypes.some(type => type === value);

export type DatasetMetadata = z.TypeOf<typeof datasetMetadataSchema>;
export type DatasetColumn = {
  name: string;
  type: ElisColumnType;
  datasetAttributeName: string;
  // Whether the inferred schema allows `null` for this column. The PATCH
  // endpoint validates `value` against this schema, so a cell may only be
  // cleared (set to null) when the column is nullable.
  nullable: boolean;
  // Raw inferred bsonType(s), preserved so the UI can surface the original
  // type for debugging — most useful when `type` maps to 'unknown'.
  bsonType: string | string[] | null | undefined;
};
export type Dataset = DatasetAPISchema & { metadata: DatasetMetadata };

export const isEditableColumn = (column: DatasetColumn): boolean =>
  column.type !== 'unknown';

export const isValidMongoKey = (key: string): boolean =>
  !key.includes('.') && !key.startsWith('$');

const bsonToElisType: Record<string, ElisColumnType> = {
  string: 'string',
  bool: 'bool',
  int: 'number',
  long: 'number',
  double: 'number',
  decimal: 'number',
  date: 'date',
  object: 'object',
  array: 'array',
};

export const mapBsonType = (
  bsonType: string | string[] | null | undefined
): ElisColumnType => {
  if (!bsonType) return 'unknown';

  if (Array.isArray(bsonType)) {
    const mapped = R.pipe(
      bsonType,
      R.filter(t => t !== 'null'),
      R.map(t => bsonToElisType[t] ?? 'unknown'),
      R.unique()
    );

    return mapped.length === 1 && mapped[0] ? mapped[0] : 'unknown';
  }

  return bsonToElisType[bsonType] ?? 'unknown';
};

const INTERNAL_COLUMN_KEYS = new Set(['_id', '__digest_md5']);

// A column is nullable when its inferred bsonType is a union that includes
// `null` (e.g. `['double', 'null']`). A bare string bsonType is not nullable.
export const isNullableBsonType = (
  bsonType: string | string[] | null | undefined
): boolean => Array.isArray(bsonType) && bsonType.includes('null');

const bsonTypeSchema = z
  .union([z.string(), z.array(z.string())])
  .nullish()
  .catch(undefined);

const schemaPropertySchema = z
  .object({ bson_type: bsonTypeSchema, bsonType: bsonTypeSchema })
  .catch({});

// MDH is moving its schema keys to snake_case; read `bson_type` and fall back
// to `bsonType` until every MDH deployment returns the new key. Parsed rather
// than read from the generated type, which no longer declares `bsonType` and
// marks `bson_type` as always present.
const readBsonType = (prop: unknown): string | string[] | null | undefined => {
  const parsed = schemaPropertySchema.parse(prop);
  return parsed.bson_type ?? parsed.bsonType;
};

export const getColumnsFromMetadata = (
  metadata: DatasetMetadata,
  schema: { properties?: Record<string, unknown> } | null | undefined
): DatasetColumn[] =>
  Object.entries(schema?.properties ?? {})
    .filter(([key]) => !INTERNAL_COLUMN_KEYS.has(key))
    .map(([key, prop]) => {
      const bsonType = readBsonType(prop);
      return {
        datasetAttributeName: key,
        name: metadata.columnNames[key] ?? key,
        type: mapBsonType(bsonType),
        nullable: isNullableBsonType(bsonType),
        bsonType,
      };
    });

// Human-readable rendering of a raw inferred bsonType, for debugging tooltips.
export const formatBsonType = (
  bsonType: string | string[] | null | undefined
): string =>
  Array.isArray(bsonType) ? bsonType.join(' | ') : (bsonType ?? '');

const parseMetadata = (metadata: unknown) =>
  datasetMetadataSchema.parse(metadata);

export const parseTableMetadata = (table: DatasetAPISchema): Dataset => ({
  ...table,
  metadata: parseMetadata(table.metadata),
});

// Datasets can have an empty metadata name, which looks broken in the UI.
// Fall back to the dataset id.
export const withDefaultName = (dataset: Dataset): Dataset =>
  dataset.metadata.name
    ? dataset
    : {
        ...dataset,
        metadata: {
          ...dataset.metadata,
          name: dataset.name,
        },
      };
